import {
  Box,
  Button,
  HStack,
  Text,
  View,
  VStack,
} from "components/ui";
import { set } from "date-fns";
import {
  ClipboardPaste,
  Copy,
  Ellipsis,
  Eye,
  ListTree,
  MapPin,
  Scissors,
  Trash,
  UserPlus,
  Users,
  X as LucideX,
  Move3D,
  Plus,
  Check,
} from "lucide-react-native";
import React, { useState, useRef, useEffect } from "react";
import {
  Platform,
  Pressable,
  Text as RNText,
  Animated,
} from "react-native";
import {
  Col,
  Container as FlexGridContainer,
  Row,
} from "react-native-flex-grid";
import Modal from "react-native-modal/dist/modal";
import {
  Menu,
  MenuOption,
  MenuOptions,
  MenuTrigger,
  renderers,
} from "react-native-popup-menu";
import { TreeNode } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";

interface Props {
  node: TreeNode | undefined;
  level: number;
  onContextMenuPress?: (action: string, node: TreeNode) => void;
}

const AdminContextMenu = ({ node, level, onContextMenuPress }: Props) => {
  const { Popover } = renderers;
  const [isOpen, setIsOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const isMobile = useIsMobile();

  useEffect(() => {
    if (confirmingDelete) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 150,
        useNativeDriver: true,
      }).start();
    } else {
      fadeAnim.setValue(0);
    }
  }, [confirmingDelete]);

  const pressableHover = {
    cursor: "pointer",
    transition: "background-color 0.15s ease-in-out",
    hover: {
      backgroundColor: "#1e4b5f",
    },
  };

  const options = [
    { title: "Move", icon: <Move3D color="white" /> },
    {
      title: "Delete",
      icon: <Trash color="white" />,
      onConfirm: () => {
        if (onContextMenuPress) {
          onContextMenuPress("Delete", node!);
        }
      },
    },
  ];

  let groupOptions = [
    { title: "Move", icon: <Move3D color="white" /> },
    { title: "Add group", icon: <Plus color="white" /> },
    {
      title: "Delete",
      icon: <Trash color="white" />,
      onConfirm: () => {
        if (onContextMenuPress) {
          onContextMenuPress("Delete", node!);
        }
      },
    },
  ];

  if (level === 0) groupOptions.shift();
  if (node?.name === "Default Group" && node?.siteId == null)
    groupOptions = groupOptions.filter((option) => option.title !== "Delete");

  const renderMenuOptions = (items: any[], paddingHorizontal: number) =>
    items.map((item, index) => {
      const isDelete = item.title.toLowerCase() === "delete";

       if (isDelete && !confirmingDelete) {
        return (
          <Pressable
            key={index}
            onPress={() => setConfirmingDelete(true)}
            style={({ hovered }) => [
              { width: "100%" },
              pressableHover,
              hovered && { backgroundColor: "#1e4b5f", borderRadius: 4 },
			 
            ]}
          >
            <Box
              className="admin-context-menu"
              paddingHorizontal={paddingHorizontal}
              paddingVertical={6}
              flexDirection="row"
              gap={8}
              alignItems="center"
			  marginLeft={4}
            >
              {item.icon}
              <Text color="$basicwhite">{item.title}</Text>
            </Box>
          </Pressable>
        );
      }

        if (isDelete && confirmingDelete) {
        return (
          <Animated.View
            key={index}
            style={{ opacity: fadeAnim, width: "100%" }}
          >
            <Box
              className="admin-context-menu"
              paddingHorizontal={paddingHorizontal}
              paddingVertical={6}
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              gap={12}
            >
              <Text color="$basicwhite">Confirm</Text>
              <Pressable
                onPress={() => setConfirmingDelete(false)}
                style={({ hovered }) => [
                  {
                    padding: 6,
                    borderRadius: 6,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  },
                  hovered && { backgroundColor: "#1e4b5f" },
                ]}
              >
                <LucideX color="#ff4d4d" size={18} />
              </Pressable>
              <Pressable
                onPress={() => {
                  setConfirmingDelete(false);
                  item.onConfirm?.();
				          setIsOpen(false);
                }}
                style={({ hovered }) => [
                  {
                    padding: 6,
                    borderRadius: 6,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  },
                  hovered && { backgroundColor: "#1e4b5f" },
                ]}
              >
                <Check color="#00cc66" size={18} />
              </Pressable>
            </Box>
          </Animated.View>
        );
      }

       return (
        <MenuOption
          key={index}
          value={item.title}
          onSelect={item.onSelect}
        >
          <Pressable style={({ hovered }) => [
            { width: "100%" },
            pressableHover,
            hovered && { backgroundColor: "#1e4b5f", borderRadius: 4 },
          ]}

 onPress={() => {
                  setConfirmingDelete(false);
                  				  setIsOpen(false);

                  if( onContextMenuPress )
                  {
                    onContextMenuPress(item.title, node!);
                  }
                }}

          >
            <Box
              className="admin-context-menu"
              paddingHorizontal={paddingHorizontal}
              paddingVertical={6}
              flexDirection="row"
              gap={8}
              alignItems="center"
            >
              {item.icon}
              <Text color="$basicwhite">{item.title}</Text>
            </Box>
          </Pressable>
        </MenuOption>
      );
    });

  if (Platform.OS === "web" && isMobile) return null;

  return (
    <Menu
      renderer={Popover}
      rendererProps={{
        container: { marginLeft: 8 },
        preferredPlacement: "right",
        anchorStyle: { background: "#173648" },
      }}
      style={{ marginLeft: "auto" }}
      onBackdropPress={() => setConfirmingDelete(false)}
      onSelect={(value) => {
        if (onContextMenuPress) {
          onContextMenuPress(value, node!);
        }
      }}
    >
      <MenuTrigger>
        <Ellipsis color={node?.name === "Default Group" ? "white" : "black"} />
      </MenuTrigger>
      <MenuOptions
        customStyles={{
          optionsWrapper: {
            borderRadius: 12,
            backgroundColor: "#173648",
            overflow: "hidden",
          },
          optionsContainer: {
            borderRadius: 12,
          },
        }}
      >
        {node?.type === "Group" && renderMenuOptions(groupOptions, 12)}
        {node?.type === "Site" && renderMenuOptions(options, 24)}
      </MenuOptions>
    </Menu>
  );
};

export default AdminContextMenu;